import { GraduationCap, ShieldCheck, MessageCircle, BadgeCheck, Linkedin } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SectionHeading from '../components/SectionHeading';
import { FadeUp } from '../components/Reveal';
import { engineer, site, waUrl, DEFAULT_WA_MESSAGE } from '../data/site';

export default function Engenheiro() {
  const msg = `Olá, Eng. Amilcar! Acessei o site do Grupo Majo, vi seu perfil de Responsável Técnico e gostaria de conversar sobre um projeto.`;

  return (
    <>
      <PageHeader
        index="02"
        eyebrow="Responsabilidade Técnica"
        title={engineer.name}
        description={engineer.title + ' · ' + engineer.crea + ' · +20 anos de experiência em obras, facilities e manutenção predial.'}
      />

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28" data-testid="engineer-profile">
        <div className="grid items-start gap-14 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5">
            <div className="relative">
              <span className="absolute -left-3 -top-3 z-10 select-none font-display text-2xl text-gold" aria-hidden="true">+</span>
              <span className="absolute -bottom-3 -right-3 z-10 select-none font-display text-2xl text-gold" aria-hidden="true">+</span>
              <img
                src={engineer.photo}
                alt={engineer.name}
                className="aspect-[4/3] w-full rounded-3xl object-cover object-center shadow-[0_40px_90px_-35px_rgba(13,33,69,0.55)]"
                data-testid="engineer-photo"
              />
            </div>
            <div className="mt-8 rounded-2xl border border-gold/30 bg-navy p-6 text-white" data-testid="engineer-crea-card">
              <p className="flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.2em] text-gold-light">
                <BadgeCheck size={15} /> Registro Profissional
              </p>
              <p className="mt-3 font-display text-2xl font-extrabold">{engineer.crea}</p>
              <p className="mt-2 text-xs leading-relaxed text-white/60">
                Registro e autorizações ativas no CREA. ART emitida para 100% dos serviços executados pelo Grupo Majo.
              </p>
            </div>
          </FadeUp>

          <div className="lg:col-span-7">
            <SectionHeading eyebrow="Perfil Profissional" title="Liderança técnica em operações multissite" description={engineer.bio} />

            <FadeUp delay={0.15} className="mt-12">
              <h3 className="flex items-center gap-3 font-display text-xl font-bold text-navy dark:text-white">
                <GraduationCap size={22} className="text-gold" /> Formações Acadêmicas
              </h3>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {engineer.degrees.map((d, i) => (
                  <div key={d} className="card-hover rounded-xl surface p-5 text-sm font-semibold leading-snug" data-testid={`degree-card-${i}`}>
                    <span className="mb-2 block font-display text-[10px] font-bold uppercase tracking-[0.2em] text-gold">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    {d}
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.2} className="mt-12">
              <h3 className="flex items-center gap-3 font-display text-xl font-bold text-navy dark:text-white">
                <ShieldCheck size={22} className="text-gold" /> Certificações
              </h3>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {engineer.certifications.map((c) => (
                  <span key={c} className="chip !px-4 !py-2">{c}</span>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.25} className="mt-12">
              <h3 className="font-display text-xl font-bold text-navy dark:text-white">Sistemas e Ferramentas</h3>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {engineer.systems.map((s) => (
                  <span key={s} className="chip !px-4 !py-2 opacity-80">{s}</span>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.3} className="mt-12 flex flex-col gap-3.5 sm:flex-row">
              <a href={waUrl(msg)} target="_blank" rel="noopener noreferrer" data-testid="engineer-whatsapp-cta" className="btn-gold">
                <MessageCircle size={17} /> Falar com o Eng. Amilcar
              </a>
              <a
                href={site.contact.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="engineer-linkedin-cta"
                className="btn-outline text-navy dark:text-white"
              >
                <Linkedin size={16} /> LinkedIn
              </a>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-[var(--surface)]" data-testid="engineer-experience">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-28">
          <SectionHeading
            eyebrow="Trajetória Profissional"
            title="Experiência executiva em grandes operações"
            description="Gestão de engenharia, facilities e manutenção em empresas nacionais e multinacionais, com resultados mensuráveis em custos, SLAs e disponibilidade operacional."
          />
          <div className="mt-14 space-y-5">
            {engineer.experience.map((e, i) => (
              <FadeUp key={e.period} delay={i * 0.05}>
                <article className="card-hover grid gap-4 rounded-2xl surface p-7 md:grid-cols-12 md:gap-8" data-testid={`experience-item-${i}`}>
                  <div className="md:col-span-3">
                    <p className="font-display text-xs font-extrabold uppercase tracking-[0.16em] text-azure dark:text-gold-light">{e.period}</p>
                    <p className="mt-1.5 text-xs text-muted-c">{e.company}</p>
                  </div>
                  <div className="md:col-span-9">
                    <h3 className="font-display text-lg font-bold leading-snug text-navy dark:text-white">{e.role}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-c">{e.highlight}</p>
                  </div>
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
